/**
 * Theme Color Mapping for PDF Generation
 *
 * Maps Oráculo dos 12 Raios theme IDs to color palettes for PDF styling.
 * Based on CSS theme variables from index.css
 */

// TD3-204: Import TOS_CONSTANTS for dynamic version in PDF footer
import { TOS_CONSTANTS } from '../constants/tosVersion';

// Theme color mappings based on CSS variables
export const THEME_COLOR_PALETTES = {
  'basic': {
    primary: '#6366F1',
    secondary: '#8B5CF6', 
    accent: '#06B6D4',
    gradient: ['#6366F1', '#8B5CF6'],
    text: '#2D3748',
    textSecondary: '#718096',
    background: '#FFFFFF'
  },

  'masculine-heart': {
    primary: '#DC2626',
    secondary: '#7C2D12',
    accent: '#F59E0B',
    gradient: ['#DC2626', '#7C2D12'],
    text: '#2D3748',
    textSecondary: '#718096', 
    background: '#FEF2F2' // Light red tint
  },

  'feminine-heart': {
    primary: '#EC4899',
    secondary: '#BE185D',
    accent: '#F472B6',
    gradient: ['#EC4899', '#BE185D'],
    text: '#2D3748',
    textSecondary: '#718096',
    background: '#FDF2F8' // Light pink tint
  },

  'masculine-wealth': {
    primary: '#059669',
    secondary: '#065F46',
    accent: '#10B981',
    gradient: ['#059669', '#065F46'],
    text: '#2D3748',
    textSecondary: '#718096',
    background: '#F0FDF4' // Light green tint
  },

  'feminine-wealth': {
    primary: '#7C3AED',
    secondary: '#5B21B6',
    accent: '#A78BFA',
    gradient: ['#7C3AED', '#5B21B6'],
    text: '#2D3748',
    textSecondary: '#718096',
    background: '#FAF5FF' // Light purple tint
  },

  'masculine-purpose': {
    primary: '#1E40AF',
    secondary: '#1E3A8A',
    accent: '#3B82F6',
    gradient: ['#1E40AF', '#1E3A8A'],
    text: '#2D3748',
    textSecondary: '#718096',
    background: '#EFF6FF' // Light blue tint
  },

  'feminine-purpose': {
    primary: '#BE185D',
    secondary: '#831843',
    accent: '#EC4899',
    gradient: ['#BE185D', '#831843'],
    text: '#2D3748',
    textSecondary: '#718096',
    background: '#FDF2F8' // Light pink tint
  },

  'masculine-neutral': {
    primary: '#374151',
    secondary: '#1F2937',
    accent: '#6B7280',
    gradient: ['#374151', '#1F2937'],
    text: '#111827',
    textSecondary: '#6B7280',
    background: '#F9FAFB' // Light gray tint
  },

  'feminine-neutral': {
    primary: '#6B7280',
    secondary: '#4B5563',
    accent: '#9CA3AF',
    gradient: ['#6B7280', '#4B5563'],
    text: '#111827',
    textSecondary: '#6B7280',
    background: '#F9FAFB' // Light gray tint
  },

  'universal-heart': {
    primary: '#F59E0B',
    secondary: '#D97706',
    accent: '#FCD34D',
    gradient: ['#F59E0B', '#D97706'],
    text: '#2D3748',
    textSecondary: '#718096',
    background: '#FFFBEB' // Light amber tint
  },

  'universal-wealth': {
    primary: '#10B981',
    secondary: '#059669',
    accent: '#34D399',
    gradient: ['#10B981', '#059669'],
    text: '#2D3748',
    textSecondary: '#718096',
    background: '#ECFDF5' // Light emerald tint
  },

  'universal-purpose': {
    primary: '#8B5CF6',
    secondary: '#7C3AED',
    accent: '#A78BFA',
    gradient: ['#8B5CF6', '#7C3AED'],
    text: '#2D3748',
    textSecondary: '#718096',
    background: '#F5F3FF' // Light violet tint
  }
};

/**
 * Get theme color palette for PDF styling
 * @param {string} themeId - Theme identifier from oracle session
 * @returns {Object} - Color palette object
 */
export const getThemeColors = (themeId = 'basic') => {
  const palette = THEME_COLOR_PALETTES[themeId];
  
  if (!palette) {
    console.warn(`Theme '${themeId}' not found, falling back to basic theme`);
    return THEME_COLOR_PALETTES.basic;
  }
  
  return palette;
};

/**
 * Create theme-aware branding configuration for PDF
 * @param {string} themeId - Theme identifier
 * @param {Object} customOverrides - Optional custom overrides
 * @returns {Object} - PDF branding configuration
 */
export const createThemeBrandingConfig = (themeId = 'basic', customOverrides = {}) => {
  const themeColors = getThemeColors(themeId);
  
  const baseBranding = {
    colors: {
      primary: themeColors.primary,
      secondary: themeColors.secondary,
      accent: themeColors.accent,
      text: themeColors.text,
      textSecondary: themeColors.textSecondary,
      background: themeColors.background,
      sectionBorder: themeColors.accent
    },

    fonts: {
      primary: 'helvetica',
      secondary: 'times',
      sizes: {
        title: 24,
        subtitle: 16,
        sectionTitle: 14,
        body: 11,
        caption: 9
      }
    },

    layout: {
      margins: {
        top: 20,
        bottom: 20,
        left: 20,
        right: 20
      },
      spacing: {
        sectionGap: 20,
        paragraphGap: 10,
        lineHeight: 6
      }
    },

    branding: {
      title: 'Chama Violeta Oracle',
      subtitle: 'Consulta Completa do Oráculo',
      // TD3-204: Dynamic version footer matching Dashboard (no hyperlink)
      footerText: `Oráculo dos 12 Raios • v${TOS_CONSTANTS.version} • by Buenaar`,
      logoUrl: null,
      watermark: null,
      themeId: themeId
    },

    // Theme-specific gradient effects
    gradients: {
      header: themeColors.gradient,
      decree: [themeColors.accent, themeColors.secondary],
      subtle: [themeColors.background, lightenColor(themeColors.accent, 90)]
    },

    // Section-specific styling with theme colors
    sections: {
      blockage: {
        borderColor: '#EF4444', // Always red for blockage
        backgroundColor: null,
        titleColor: themeColors.primary
      },
      solution: {
        borderColor: '#10B981', // Always green for solution
        backgroundColor: null,
        titleColor: themeColors.primary
      },
      wisdom: {
        borderColor: themeColors.accent,
        backgroundColor: null,
        titleColor: themeColors.primary
      },
      decree: {
        borderColor: themeColors.accent,
        backgroundColor: lightenColor(themeColors.accent, 95),
        titleColor: themeColors.secondary,
        fontStyle: 'italic',
        fontWeight: 'bold'
      }
    }
  };

  // Deep merge with custom overrides
  return deepMerge(baseBranding, customOverrides);
};

/**
 * Get theme display name for PDF
 * @param {string} themeId - Theme identifier
 * @returns {string} - Human readable theme name
 */
export const getThemeDisplayName = (themeId = 'basic') => {
  const themeNames = {
    'basic': 'Básico',
    'masculine-heart': 'Masculino Coração',
    'feminine-heart': 'Feminino Coração',
    'masculine-wealth': 'Masculino Prosperidade',
    'feminine-wealth': 'Feminino Prosperidade',
    'masculine-purpose': 'Masculino Propósito',
    'feminine-purpose': 'Feminino Propósito',
    'masculine-neutral': 'Masculino Neutro',
    'feminine-neutral': 'Feminino Neutro',
    'universal-heart': 'Universal Coração',
    'universal-wealth': 'Universal Prosperidade',
    'universal-purpose': 'Universal Propósito'
  };
  
  return themeNames[themeId] || 'Básico';
};

/**
 * Check if theme has warm or cool color palette
 * @param {string} themeId - Theme identifier
 * @returns {string} - 'warm' or 'cool'
 */
export const getThemeTemperature = (themeId = 'basic') => {
  const warmThemes = ['masculine-heart', 'feminine-heart', 'universal-heart', 'masculine-wealth', 'feminine-wealth', 'universal-wealth'];
  return warmThemes.includes(themeId) ? 'warm' : 'cool';
};

// Utility functions
function lightenColor(hex, percent) {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt;
  const G = (num >> 8 & 0x00FF) + amt;
  const B = (num & 0x0000FF) + amt;
  
  return '#' + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
    (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
    (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1);
}

function deepMerge(target, source) {
  const output = { ...target };
  
  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach(key => {
      if (isObject(source[key])) {
        if (!(key in target)) {
          Object.assign(output, { [key]: source[key] });
        } else {
          output[key] = deepMerge(target[key], source[key]);
        }
      } else {
        Object.assign(output, { [key]: source[key] });
      }
    });
  }
  
  return output;
}

function isObject(item) {
  return item && typeof item === 'object' && !Array.isArray(item);
}

export default {
  THEME_COLOR_PALETTES,
  getThemeColors,
  createThemeBrandingConfig,
  getThemeDisplayName,
  getThemeTemperature
};